import { useEffect, useRef, useState } from 'react';
import PlotCard from '@/components/gallery/plot-card';
import { Card } from '@/components/ui/card';
import type { FileObject } from '@shared/schema';
import { authorizedFetch } from '@/lib/queryClient';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useToast } from '@/hooks/use-toast';
import { useProjectGallery } from '@/hooks/use-project-gallery';
import { useAuth } from '@/auth/AuthProvider';

export default function ProjectGallerySection({
  files,
  projectId,
  onView,
  onEdit,
  onDuplicate,
}: {
  files: FileObject[];
  projectId: string;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate?: (plot: any) => void;
}) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'title'>('recent');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterDataset, setFilterDataset] = useState<string>('all');
  const { go } = useNav();
  const { user } = useAuth();
  const { toast } = useToast();

  const getFileName = (file?: FileObject) => {
    if (!file) return undefined;
    const meta = (file.metadata as Record<string, any> | null | undefined) ?? {};
    return meta.originalName || meta.filename || file.objectPath;
  };
  const datasetCacheRef = useRef<Map<string, { id: string | null; name?: string }>>(new Map());

  // Use the new gallery hook
  const gallery = useProjectGallery({
    projectId,
    search,
    sort: sortBy,
    datasetId: filterDataset !== 'all' ? filterDataset : null,
    limit: 6, // Show 6 plots in inline gallery
    enabled: !!projectId && !!user,
  });

  // Listen for duplication events to refresh
  useEffect(() => {
    const onDuplicated = (e: any) => {
      try {
        const pid = e?.detail?.projectId;
        if (!projectId || (pid && pid !== projectId)) return;
        gallery.invalidate();
      } catch {}
    };
    window.addEventListener('lumiplot-duplicated' as any, onDuplicated);
    return () => {
      window.removeEventListener('lumiplot-duplicated' as any, onDuplicated);
    };
  }, [projectId, gallery]);

  // Map gallery items to PlotCard format with client-side filtering
  const enhancedPlots = gallery.items
    .map((item) => {
      // Cache dataset info for navigation
      if (item.datasetFileId || item.datasetName) {
        datasetCacheRef.current.set(item.id, {
          id: item.datasetFileId,
          name: item.datasetName || undefined,
        });
      }

      return {
        id: item.id,
        title: item.title || 'Untitled Plot',
        createdAt: new Date(item.createdAt),
        thumb_url: item.thumbnailUrl || item.preview || undefined,
        datasetName: item.datasetName || undefined,
        datasetFileId: item.datasetFileId,
        plotSpec: item.chartType ? { plotType: item.chartType } : undefined,
        plotlyJson: item.chartType ? { data: [{ type: item.chartType }] } : undefined,
        _type: item.chartType || 'bar',
      };
    })
    .filter((p: any) => {
      // Client-side filtering for chart type (server doesn't support this yet)
      if (filterType !== 'all' && p._type !== filterType) return false;
      return true;
    });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold">Gallery</h2>
      </div>

      {/* Controls */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plots..."
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          >
            <option value="recent">Sort: Recent</option>
            <option value="title">Sort: Title</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-input border border-border rounded px-3 py-2 text-sm capitalize"
          >
            <option value="all">Filter: All types</option>
            <option value="bar">bar</option>
            <option value="scatter">scatter</option>
            <option value="line">line</option>
            <option value="pie">pie</option>
            <option value="area">area</option>
            <option value="box">box</option>
            <option value="heatmap">heatmap</option>
          </select>
          <select
            value={filterDataset}
            onChange={(e) => setFilterDataset(e.target.value)}
            className="bg-input border border-border rounded px-3 py-2 text-sm"
          >
            <option value="all">Filter: All datasets</option>
            {files.map((f) => (
              <option key={f.id} value={String(f.id)}>{getFileName(f) || f.id}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Grid */}
      {gallery.isLoading && enhancedPlots.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading plots...</div>
      ) : enhancedPlots.length === 0 ? (
        <div className="text-sm text-muted-foreground">No plots yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {enhancedPlots.map((p: any) => (
            <PlotCard
              key={p.id}
              plot={p}
              datasetName={p.datasetName}
              onDatasetClick={async () => {
                try {
                  if (!projectId) return;
                  const cached = datasetCacheRef.current.get(p.id);
                  let datasetId = cached?.id ?? (p.datasetFileId ? String(p.datasetFileId) : null);
                  let datasetName = cached?.name ?? (p.datasetName || undefined);

                  if (!datasetId) {
                    try {
                      const res = await authorizedFetch(`/api/ai/v2/plots/${p.id}/versions/latest/meta.json`);
                      if (res.ok) {
                        const meta = await res.json();
                        datasetId = meta?.datasetFileId || meta?.dataset_file_id || datasetId;
                        datasetName = datasetName ?? meta?.datasetName ?? meta?.dataset_name;
                      }
                    } catch (err) {
                      console.warn('Failed to load meta for dataset navigation', err);
                    }
                  }

                  if (!datasetId && datasetName) {
                    const match = files.find((f) => String(getFileName(f)).toLowerCase() === String(datasetName).toLowerCase());
                    if (match) datasetId = String(match.id);
                  }

                  if (datasetId) {
                    datasetCacheRef.current.set(p.id, { id: datasetId, name: datasetName });
                    go(routes.project.file(projectId, datasetId));
                  } else {
                    toast({ title: 'Dataset unavailable', description: 'Could not locate the dataset for this plot.', variant: 'destructive' });
                  }
                } catch (err) {
                  console.error('Dataset navigation failed', err);
                  toast({ title: 'Dataset unavailable', description: 'Could not locate the dataset for this plot.', variant: 'destructive' });
                }
              }}
              onView={() => onView(p.id)}
              onEdit={() => onEdit(p.id)}
              onDuplicate={() => onDuplicate?.(p)}
              onAddToFigure={() => go(routes.project.figures.new(projectId) + `?plotId=${p.id}`)}
              projectId={projectId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
